/**
 * WorkspaceHero — Cruise Command's standard operational workspace hero.
 *
 * PO decision 2026-02-20: The hero formerly known as `VoyageHero` is
 * no longer voyage-specific — it is the standard editorial hero for
 * every operational workspace (Voyage Home, Add-Ons, Binder, Finance,
 * Documents, …). Each workspace passes an accent color and an
 * eyebrow label so the traveler sees a consistent editorial voice
 * while still knowing which workspace they're in.
 *
 * Fields (phase-aware, in order):
 *   1. Eyebrow (top-left)             — accent-colored workspace tag
 *                                       (falls back to the phase pill
 *                                       when no eyebrow is provided).
 *   2. Optional primary action        — top-right pill (e.g. "Publish Cruise Binder")
 *   3. Big serif headline             — cruise name (voyage.name)
 *   4. Meta line                      — Ship Name · date range
 *   5. Contextual block               — phase-appropriate:
 *        · PRE_CRUISE      → big countdown  ("97 days to sail")
 *        · EMBARKATION     → "Welcome aboard · Boarding today"
 *        · AT_SEA          → "Day X of Y · Z days remaining"
 *        · IN_PORT         → "In [Port] · All aboard [time]"
 *        · DISEMBARKATION  → "Final morning · Journey home"
 *        · POST_CRUISE     → "Welcome home"
 *
 * Motion (2026-02-17 · P3): phase transitions crossfade calmly rather
 * than snapping. Motion is purposeful, not decorative, in line with
 * the Cruise Command motion principles.
 *
 * Composition:
 *   • Renders through <CruiseHeroFrame> so it inherits the shared
 *     ship-photograph pipeline (Ken Burns, focal-point-aware crop,
 *     environmental mood wash, warm colour grading, base skeleton).
 */
import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import CruiseHeroFrame from "@/components/planning/CruiseHeroFrame";
import { Printer, ArrowRight } from "lucide-react";

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const PHASE_META = {
  PRE_CRUISE:     { label: "PRE-CRUISE",     tone: "#3ea1ff" },
  EMBARKATION:    { label: "EMBARKATION",    tone: "#d4af37" },
  IN_PORT:        { label: "IN PORT",        tone: "#22c55e" },
  AT_SEA:         { label: "AT SEA",         tone: "#3ea1ff" },
  DISEMBARKATION: { label: "DISEMBARKATION", tone: "#f97316" },
  POST_CRUISE:    { label: "POST-CRUISE",    tone: "#94a3b8" },
};

/**
 * @typedef WorkspaceHeroPrimaryAction
 * @property {string} label
 * @property {() => void} onClick
 * @property {string} [testId]
 * @property {"printer"|"arrow"|null} [icon="printer"]
 *
 * @typedef WorkspaceHeroCountdown
 * @property {number} value              Big number (e.g. 97).
 * @property {string} unitLabel          Overline label (e.g. "days to sail").
 *
 * @typedef WorkspaceHeroProps
 * @property {string} [shipName]             Line 1 — ship name (Archivo Bold, primary display).
 * @property {string} [voyageLabel]          Line 2 — voyage identifier (e.g. "October 2026"),
 *                                           Archivo Semibold, ~70% of ship size.
 * @property {string} [dateRange]            Line 3 — sailing dates (Poppins Regular).
 * @property {string} [cruiseName]           Deprecated fallback for shipName.
 * @property {WorkspaceHeroCountdown} [countdown]   Countdown block (pre-cruise emphasis).
 * @property {string} [contextLine]          Single-line phase-appropriate context
 *                                           (used when there is no countdown).
 * @property {string} [phase]                Phase code — drives the phase pill and
 *                                           the crossfade transition key.
 * @property {WorkspaceHeroPrimaryAction} [primaryAction]  Optional top-right action pill.
 * @property {object} [heroImage]            Backend hero payload.
 * @property {string} [testId="voyage-hero"]
 */

const ActionIcon = ({ icon }) => {
  if (icon === "arrow") return <ArrowRight className="w-3 h-3" />;
  if (icon === null)    return null;
  return <Printer className="w-3 h-3" />;
};

export function WorkspaceHero({
  shipName,
  voyageLabel,
  dateRange,
  cruiseName,
  countdown,
  contextLine,
  phase,
  primaryAction,
  heroImage,
  eyebrow,        // { label: string, color?: string } · workspace-scoped tag
  accentColor,    // override for the ship-name / eyebrow accent (default #c28e42)
  baseGradient,   // accent-tinted gradient background (Operational heroes
                  // without a ship photograph — Canonical Hero Colour
                  // Matrix, PO 2026-02-14). Applied *inside* the safe
                  // zone, so Voyage Home's ship photograph continues to
                  // dominate whenever an image is provided.
  testId = "workspace-hero",
}) {
  const phaseMeta = phase ? PHASE_META[phase] : null;
  const hasCountdown =
    countdown && typeof countdown.value === "number" && countdown.value > 0;
  const shipAccent = accentColor || "#c28e42";
  const eyebrowColor = eyebrow?.color || shipAccent;
  const showGradient = Boolean(baseGradient) && !heroImage?.image_url;

  return (
    <CruiseHeroFrame
      hero={heroImage || {}}
      backendBase={BACKEND_BASE}
      minHeight={280}
      testId={testId}
    >
      {/* Accent-tinted gradient background · Canonical Hero Colour
          Matrix (PO 2026-02-14). Only rendered when a workspace
          declares a `baseGradient` AND the frame has no ship
          photograph — Voyage Home continues to dominate with its
          image, while Journey / Briefing / Route Map each carry a
          distinct workspace identity gradient. */}
      {showGradient && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          data-testid={`${testId}-base-gradient`}
          style={{ background: baseGradient }}
        />
      )}

      {/* Eyebrow — top-left. A workspace-scoped `eyebrow` prop takes
          precedence over the phase pill, so Add-Ons / Binder / Finance
          etc. can identify themselves in their own accent color while
          Voyage Home (which passes `phase`) keeps the phase pill. */}
      <AnimatePresence mode="wait" initial={false}>
        {eyebrow?.label ? (
          <motion.div
            key={`eyebrow-${eyebrow.label}`}
            className="absolute top-4 left-5 inline-flex items-center gap-2 pointer-events-none"
            data-testid={`${testId}-eyebrow`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: eyebrowColor, boxShadow: `0 0 8px ${eyebrowColor}` }}
              aria-hidden
            />
            <span
              className="font-body text-label font-semibold uppercase tracking-[0.14em]"
              style={{ color: eyebrowColor }}
            >
              {eyebrow.label}
            </span>
          </motion.div>
        ) : phaseMeta ? (
          <motion.div
            key={`phase-${phase}`}
            className="absolute top-4 left-5 inline-flex items-center gap-2 pointer-events-none"
            data-testid={`${testId}-phase-badge`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: phaseMeta.tone, boxShadow: `0 0 8px ${phaseMeta.tone}` }}
              aria-hidden
            />
            <span className="font-body text-label text-white/90">
              {phaseMeta.label}
            </span>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Primary action — top-right. Reduced text size (2026-02-17 · P3)
          so it reads as a secondary action rather than competing with
          the ship name / countdown. */}
      {primaryAction && (
        <button
          type="button"
          onClick={primaryAction.onClick}
          data-testid={primaryAction.testId || `${testId}-primary-action`}
          className="absolute top-3.5 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/12 hover:bg-white/22 backdrop-blur border border-white/22 text-white/90 font-body text-[9.5px] font-semibold uppercase tracking-[0.14em] transition-colors"
        >
          <ActionIcon icon={primaryAction.icon} />
          {primaryAction.label}
        </button>
      )}

      {/* Content — anchored left, vertically centred inside the safe zone.
          The context block (countdown OR phase line) crossfades on
          phase changes; identity remains steady. */}
      <div className="absolute inset-y-0 left-0 flex items-center px-6 lg:px-9 max-w-[560px] w-[min(560px,60%)] pointer-events-none">
        <div className="pointer-events-auto">
          {/* Line 1 · Primary display title.

              PO 2026-02-15 · RC7 typography correction. WorkspaceHero
              renders the ship name ("Symphony of the Seas"), which is
              a multi-word identity that wraps to two lines. At the
              previous scale of `clamp(32px, 3.8vw, 46px)` — inherited
              from the Utility Hero (`IntegratedWorkspace`) where the
              title is a short single-line phrase like "Your Journey" —
              the wrapped ship name read as a billboard and overwhelmed
              the voyage identity + the ship photograph. Reduced to
              `clamp(24px, 2.4vw, 32px)` here so the ship name feels
              like a premium editorial headline. Font family, weight,
              colour, positioning, tracking and the two-line responsive
              wrap are all preserved. Line-height stays tight so the
              two-line title remains compact. The Utility Hero size is
              intentionally NOT changed — its titles are short and do
              not exhibit the same regression. */}
          <h2
            className="font-display font-black leading-[0.98] tracking-tight"
            style={{ color: shipAccent, fontSize: "clamp(24px, 2.4vw, 32px)" }}
            data-testid={`${testId}-ship`}
          >
            {shipName || cruiseName || "Your cruise"}
          </h2>

          {/* Line 2 · Voyage label (Archivo Semibold, ~70% of ship size,
              white, generous spacing). */}
          {voyageLabel && (
            <div
              className="mt-2 font-display text-white text-[22px] lg:text-[26px] leading-[1.1] font-semibold"
              data-testid={`${testId}-voyage`}
            >
              {voyageLabel}
            </div>
          )}

          {/* Line 3 · Sailing dates (Poppins Regular, smaller metadata). */}
          {dateRange && (
            <div
              className="mt-3 text-white/85 font-body text-body-sm font-normal"
              data-testid={`${testId}-dates`}
            >
              {dateRange}
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            {hasCountdown ? (
              <motion.div
                key={`countdown-${phase}-${countdown.value}`}
                className="mt-5 flex flex-col items-start text-white"
                data-testid={`${testId}-countdown`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
              >
                <span className="font-display text-[56px] lg:text-[68px] leading-[0.95] font-black tabular-nums">
                  {countdown.value}
                </span>
                <span className="mt-1 font-body text-label text-white/75">
                  {countdown.unitLabel}
                </span>
              </motion.div>
            ) : contextLine ? (
              <motion.div
                key={`ctx-${phase}-${contextLine}`}
                className="mt-5 text-white font-body text-body-lg font-medium"
                data-testid={`${testId}-context`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
              >
                {contextLine}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </CruiseHeroFrame>
  );
}

export default WorkspaceHero;
